import React, { useState } from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    Chip, 
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    AccordionGroup,
    Divider,
    List,
    ListItem,
    ListItemDecorator,
    Sheet
} from '@mui/joy';
import { useColorScheme } from '@mui/joy/styles';
import SEO from '../components/common/SEO';

const jobListings = [
    {
        id: 'backend',
        title: 'Backend Engineer',
        type: 'Full-time',
        location: 'Nairobi, Museum Hill',
        department: 'Engineering',
        icon: '⚙️',
        color: 'primary',
        description: "We're seeking a talented Back-End Software Engineer to join Hubitable. You'll architect and build the robust, scalable back-end systems that power Hubitable's engagement features—handling everything from real-time customer orders to secure payment integrations and data-driven personalization.",
        highlights: [
            'Design, develop, and optimize RESTful APIs and microservices using modern back-end technologies (e.g., Node.js, Python/Django, Docker Swarm)',
            'Integrate third-party services like payment gateways (e.g., M-Pesa, Stripe) and real-time databases (e.g., MongoDB, PostgreSQL)',
            'Write clean, testable code; conduct code reviews; and automate CI/CD pipelines'
        ],
        requirements: [
            { title: 'Technical Prowess', desc: "Bachelor's degree in CS or equivalent. 2+ years of professional back-end development with expertise in algorithms, data structures, and OOP principles" },
            { title: 'Problem-Solving Skills', desc: 'Proven track record of tackling real-world challenges—like optimizing query performance or securing sensitive user data' },
            { title: 'Tech Savvy', desc: 'Familiarity with cloud platforms and containerization (Docker/Kubernetes)' },
            { title: 'Soft Skills', desc: 'Good communication; adaptability in a fast-paced startup; collaborative spirit' }
        ],
        applyInstructions: {
            format: [
                'Two paragraphs on background & why you\'re interested',
                'GitHub Profile Link (highlight up to 5 projects)',
                'StackOverflow Link (optional but recommended)',
                'LeetCode Link (optional but recommended)',
                'Huggingface Link (optional but recommended)'
            ],
            email: 'hi@hubitable.com',
            subject: 'Software Engineer – [Your Name]'
        }
    },
    {
        id: 'react-native',
        title: 'React Native Engineer',
        type: 'Full-time',
        location: 'Nairobi, Museum Hill',
        department: 'Mobile',
        icon: '📱',
        color: 'success',
        description: "We're looking for a passionate React Native Engineer to build beautiful, performant mobile experiences. From seamless animations to offline-first architecture, you'll craft the app experiences that make Hubitable indispensable to health-conscious individuals.",
        highlights: [
            'Build and maintain cross-platform mobile applications using React Native with pixel-perfect UI/UX',
            'Implement complex features including real-time notifications, location-based services, and smooth payment flows',
            'Optimize app performance, reduce bundle sizes, and ensure buttery-smooth 60fps animations',
            'Collaborate with designers to translate Figma mockups into responsive, accessible mobile interfaces'
        ],
        requirements: [
            { title: 'Technical Prowess', desc: "Bachelor's degree in CS or equivalent. 2+ years of React Native development with published apps on App Store/Google Play" },
            { title: 'React Native Mastery', desc: 'Deep understanding of React Native internals, navigation libraries, state management (Redux, Zustand, Context API)' },
            { title: 'Mobile-First Mindset', desc: 'Experience with offline storage, push notifications, deep linking, and performance profiling' },
            { title: 'UI/UX Sensibility', desc: 'Strong eye for design, experience with animation libraries (Reanimated, Lottie)' }
        ],
        applyInstructions: {
            format: [
                'Two paragraphs on background & why you\'re excited about mobile development',
                'GitHub Profile Link (highlight up to 5 mobile projects)',
                'App Store / Play Store Links (if available)',
                'StackOverflow Link (optional but recommended)',
                'Open Source React Native Contributions (optional)'
            ],
            email: 'hi@hubitable.com',
            subject: 'React Native Engineer – [Your Name]'
        }
    }
];

const JobCard = ({ job, expanded, onToggle }) => {
    const { mode } = useColorScheme();
    const isDark = mode === 'dark';

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 3,
                borderRadius: 'xl',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                border: expanded ? `2px solid var(--joy-palette-${job.color}-500)` : '1px solid',
                borderColor: expanded ? `${job.color}.500` : 'divider',
                boxShadow: expanded ? 'lg' : 'sm',
                '&:hover': {
                    boxShadow: 'lg',
                    transform: 'translateY(-2px)'
                }
            }}
        >
            {/* Header Section */}
            <Box
                sx={{
                    background: isDark 
                        ? `linear-gradient(135deg, var(--joy-palette-${job.color}-800) 0%, var(--joy-palette-${job.color}-900) 100%)`
                        : `linear-gradient(135deg, var(--joy-palette-${job.color}-50) 0%, var(--joy-palette-${job.color}-100) 100%)`,
                    p: 3,
                    cursor: 'pointer'
                }}
                onClick={onToggle}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                fontSize: '2.5rem',
                                width: 60,
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 'lg',
                                bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'background.surface',
                                boxShadow: 'sm'
                            }}
                        >
                            {job.icon}
                        </Box>
                        <Box>
                            <Typography level="h3" sx={{ mb: 0.5 }}>
                                {job.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip size="sm" variant="soft" color={job.color}>{job.type}</Chip>
                                <Chip size="sm" variant="outlined">{job.location}</Chip>
                                <Chip size="sm" variant="plain">{job.department}</Chip>
                            </Box>
                        </Box>
                    </Box>
                    <Button 
                        variant={expanded ? 'solid' : 'outlined'} 
                        color={job.color}
                        size="lg"
                        sx={{ borderRadius: 'xl' }}
                    >
                        {expanded ? 'Close' : 'View Details'}
                    </Button>
                </Box>
            </Box>

            {/* Expanded Content */}
            {expanded && (
                <CardContent sx={{ p: 4 }}>
                    {/* Description */}
                    <Typography level="body-lg" sx={{ mb: 4, lineHeight: 1.8 }}>
                        {job.description}
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    {/* Day-to-Day Highlights */}
                    <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        ✨ Day-to-Day Highlights
                    </Typography>
                    <List sx={{ mb: 4 }}>
                        {job.highlights.map((item, idx) => (
                            <ListItem key={idx} sx={{ py: 1 }}>
                                <ListItemDecorator sx={{ color: `${job.color}.500` }}>▸</ListItemDecorator>
                                <Typography level="body-md">{item}</Typography>
                            </ListItem>
                        ))}
                    </List>

                    {/* Requirements */}
                    <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        🎯 What We're Looking For
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 4 }}>
                        {job.requirements.map((req, idx) => (
                            <Sheet
                                key={idx}
                                variant="soft"
                                sx={{ p: 2, borderRadius: 'lg' }}
                            >
                                <Typography level="title-sm" sx={{ mb: 0.5, fontWeight: 'lg' }}>
                                    {req.title}
                                </Typography>
                                <Typography level="body-sm">
                                    {req.desc}
                                </Typography>
                            </Sheet>
                        ))}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* How to Apply */}
                    <Sheet
                        variant="outlined"
                        sx={{
                            p: 3,
                            borderRadius: 'xl',
                            background: isDark 
                                ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
                                : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                        }}
                    >
                        <Typography level="h4" sx={{ mb: 2 }}>
                            📧 How to Apply
                        </Typography>
                        <Typography level="body-md" sx={{ mb: 2, fontWeight: 'lg', color: 'warning.600' }}>
                            Don't Send Your Resume!
                        </Typography>
                        <Typography level="body-md" sx={{ mb: 2 }}>
                            Send an email with the following:
                        </Typography>
                        <List sx={{ mb: 3 }}>
                            {job.applyInstructions.format.map((item, idx) => (
                                <ListItem key={idx} sx={{ py: 0.5 }}>
                                    <ListItemDecorator sx={{ color: `${job.color}.500` }}>•</ListItemDecorator>
                                    <Typography level="body-md">{item}</Typography>
                                </ListItem>
                            ))}
                        </List>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography level="body-md">
                                <strong>Email:</strong>{' '}
                                <a href={`mailto:${job.applyInstructions.email}`} style={{ color: 'inherit' }}>
                                    {job.applyInstructions.email}
                                </a>
                            </Typography>
                            <Typography level="body-md">
                                <strong>Subject:</strong> "{job.applyInstructions.subject}"
                            </Typography>
                        </Box>
                        <Button
                            component="a"
                            href={`mailto:${job.applyInstructions.email}?subject=${encodeURIComponent(job.applyInstructions.subject)}`}
                            variant="solid"
                            color={job.color}
                            size="lg"
                            sx={{ mt: 3, borderRadius: 'xl' }}
                        >
                            Apply Now
                        </Button>
                    </Sheet>
                </CardContent>
            )}
        </Card>
    );
};

const CareersPage = () => {
    const [expandedJob, setExpandedJob] = useState(null);
    const { mode } = useColorScheme();
    const isDark = mode === 'dark';

    const handleToggle = (jobId) => {
        setExpandedJob(expandedJob === jobId ? null : jobId);
    };

    return (
        <>
            <SEO 
                title="Careers at Hubitable - Join Our Team"
                description="Join Hubitable and help us make healthy living the easy lifestyle. We're hiring talented engineers who are passionate about building technology that matters."
            />

            {/* Hero Section */}
            <Box
                sx={{
                    minHeight: '50vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 3,
                    py: 8,
                    background: isDark
                        ? 'linear-gradient(180deg, #132f4c 0%, #0a1929 100%)'
                        : 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Decorative Elements */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '10%',
                        left: '5%',
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%)',
                        filter: 'blur(40px)'
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '10%',
                        right: '10%',
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%)',
                        filter: 'blur(50px)'
                    }}
                />

                <Typography
                    level="h1"
                    sx={{
                        fontSize: { xs: '2.5rem', md: '4rem' },
                        fontWeight: 800,
                        mb: 2,
                        background: isDark
                            ? 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)'
                            : 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    Join Our Team
                </Typography>
                <Typography
                    level="h3"
                    sx={{
                        fontWeight: 400,
                        maxWidth: 700,
                        mb: 3,
                        color: isDark ? 'neutral.300' : 'neutral.600'
                    }}
                >
                    Help us make healthy living the easy lifestyle
                </Typography>
                <Typography
                    level="body-lg"
                    sx={{
                        maxWidth: 600,
                        color: isDark ? 'neutral.400' : 'neutral.500',
                        lineHeight: 1.8
                    }}
                >
                    We're building technology that empowers health-conscious individuals and enables chefs to turn their passion into profit. Join a team that values innovation, collaboration, and making a real impact.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Chip variant="soft" color="primary" size="lg">🚀 Fast-paced startup</Chip>
                    <Chip variant="soft" color="success" size="lg">🌍 Nairobi-based</Chip>
                    <Chip variant="soft" color="warning" size="lg">💡 Innovation-driven</Chip>
                </Box>
            </Box>

            {/* Open Positions Section */}
            <Box
                sx={{
                    maxWidth: 1000,
                    mx: 'auto',
                    px: 3,
                    py: 8
                }}
            >
                <Typography
                    level="h2"
                    sx={{
                        textAlign: 'center',
                        mb: 1,
                        fontWeight: 700
                    }}
                >
                    Open Positions
                </Typography>
                <Typography
                    level="body-lg"
                    sx={{
                        textAlign: 'center',
                        mb: 6,
                        color: isDark ? 'neutral.400' : 'neutral.600'
                    }}
                >
                    {jobListings.length} role{jobListings.length !== 1 ? 's' : ''} currently available
                </Typography>

                {jobListings.map((job) => (
                    <JobCard
                        key={job.id}
                        job={job}
                        expanded={expandedJob === job.id}
                        onToggle={() => handleToggle(job.id)}
                    />
                ))}
            </Box>
        </>
    );
};

export default CareersPage;
